import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Modal,
  TextField,
  MenuItem,
  Card,
  CardContent,
  Paper,
  Grid,
  Snackbar,
  Alert,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import useAuthenticatedFetch from "../hooks/useAuthenticatedFetch";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import LocalizedTextField from "../components/common/LocalizedTextField";

const reasons = ["yorkseit", "refoua chelema", "atslaha", "other"];

const SponsorManager = ({ communityId }) => {
  const fetchWithAuth = useAuthenticatedFetch();
  const { t } = useTranslation();
  const [sponsors, setSponsors] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [editingSponsor, setEditingSponsor] = useState(null);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [activeAssignments, setActiveAssignments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingAssignment, setIsSavingAssignment] = useState(false);
  const [isSavingSponsor, setIsSavingSponsor] = useState(false);
  const [openAssignmentModal, setOpenAssignmentModal] = useState(false);
  const [openSponsorModal, setOpenSponsorModal] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm")); // Vérifie si l'écran est petit
  
  const handleEditSponsor = (sponsor) => {
    setEditingSponsor(sponsor);
    setNewSponsor(sponsor);
    setOpenSponsorModal(true);
  };

  const handleEditAssignment = (assignment) => {
    setEditingAssignment(assignment);
    setNewAssignment(assignment);
    setOpenAssignmentModal(true);
  };

  // State for forms
  const [newAssignment, setNewAssignment] = useState({
    sponsorId: "",
    communityId: "",
    startDate: "",
    endDate: "",
    reason: "",
    comment: "",
  });

  const [newSponsor, setNewSponsor] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  useEffect(() => {
    fetchData();
  }, [communityId]);

  const fetchData = () => {
    setIsLoading(true);
    Promise.all([
      fetchWithAuth(`${process.env.REACT_APP_BASE_URL}/sponsor/getSponsors/${communityId}`).then((res) => res.json()),
      fetchWithAuth(`${process.env.REACT_APP_BASE_URL}/sponsor/getSponsorsAssignments/${communityId}`).then((res) =>
        res.json()
      ),
    ])
      .then(([sponsorsData, assignmentsData]) => {
        setSponsors(sponsorsData);
        setAssignments(assignmentsData);
        filterActiveAssignments(assignmentsData);
      })
      .catch((error) => {
        console.error(error);
        showSnackbar(t("common.error.loadData"), "error");
      })
      .finally(() => setIsLoading(false));
  };

  const filterActiveAssignments = (assignments) => {
    const today = new Date().toISOString().split("T")[0];
    const active = assignments.filter(
      (assignment) => assignment.startDate <= today && (!assignment.endDate || assignment.endDate >= today)
    );
    setActiveAssignments(active);
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleAssignmentChange = (field, value) => {
    setNewAssignment((prev) => ({ ...prev, [field]: value }));
  };

  const handleSponsorChange = (field, value) => {
    setNewSponsor((prev) => ({ ...prev, [field]: value }));
  };

  const handleDeleteSponsor = (sponsorId) => {
    if (window.confirm(t("sponsors.confirmDeleteSponsor"))) {
      fetchWithAuth(`${process.env.REACT_APP_BASE_URL}/sponsor/deleteSponsor/${sponsorId}`, {
        method: "DELETE",
      })
        .then(() => {
          showSnackbar(t("sponsors.success.sponsorDeleted"), "success");
          fetchSponsors(); // Rafraîchit la liste des sponsors
        })
        .catch((error) => {
          console.error(error);
          showSnackbar(t("sponsors.error.deleteSponsor"), "error");
        });
    }
  };
  
  const handleDeleteAssignment = (communityId, sponsorId, startDate) => {
    if (window.confirm(t("sponsors.confirmDeleteAssignment"))) {
      const formattedStartDate = new Date(startDate).toISOString().split('T')[0];

      fetchWithAuth(`${process.env.REACT_APP_BASE_URL}/sponsor/deleteSponsorCommunity`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ communityId, sponsorId,  startDate: formattedStartDate }),
      })
        .then(() => {
          showSnackbar(t("sponsors.success.assignmentDeleted"), "success");
          fetchAssignments(); // Refresh assignments
        })
        .catch((error) => {
          console.error(error);
          showSnackbar(t("sponsors.error.deleteAssignment"), "error");
        });
    }
  };

  const handleSaveAssignment = () => {
    if (!newAssignment.sponsorId ||!newAssignment.startDate || !newAssignment.reason) {
      showSnackbar(t("sponsors.error.missingFields"), "error");
      return;
    }
    const formattedStartDate = new Date(newAssignment.startDate).toISOString().split("T")[0];
    const formattedEndDate = newAssignment.endDate
      ? new Date(newAssignment.endDate).toISOString().split("T")[0]
      : null;

    const payload = {
      sponsorId: newAssignment.sponsorId,
      communityId: communityId,
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      reason: newAssignment.reason,
      comment: newAssignment.comment,
    };
    setIsSavingAssignment(true);
    const url = editingAssignment
      ? `${process.env.REACT_APP_BASE_URL}/sponsor/updateSponsorAssignment`
      : `${process.env.REACT_APP_BASE_URL}/sponsor/createSponsorAssignment/${communityId}`;

    const method = editingAssignment ? "PUT" : "POST";

    fetchWithAuth(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then(() => {
        showSnackbar(
          t(editingAssignment ? "sponsors.success.assignmentUpdated" : "sponsors.success.assignmentCreated"),
          "success"
        );
        setNewAssignment({ sponsorId: "", communityId: "", startDate: "", endDate: "", reason: "", comment: "" });
        setOpenAssignmentModal(false);
        setEditingAssignment(null);
        fetchAssignments(); // Refresh assignments
      })
      .catch((error) => {
        console.error(error);
        showSnackbar(
          t(editingAssignment ? "sponsors.error.updateAssignment" : "sponsors.error.saveAssignment"),
          "error"
        );
      })
      .finally(() => setIsSavingAssignment(false));
  };

 const handleSaveSponsor = () => {
    if (!newSponsor.firstName || !newSponsor.lastName) {
      showSnackbar(t("sponsors.error.missingFields"), "error");
      return;
    }

    setIsSavingSponsor(true);
    const url = editingSponsor
      ? `${process.env.REACT_APP_BASE_URL}/sponsor/updateSponsor/${editingSponsor.id}`
      : `${process.env.REACT_APP_BASE_URL}/sponsor/createSponsor/${communityId}`;

    const method = editingSponsor ? "PUT" : "POST";

    fetchWithAuth(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(newSponsor),
    })
      .then(() => {
        showSnackbar(
          t(editingSponsor ? "sponsors.success.sponsorUpdated" : "sponsors.success.sponsorCreated"),
          "success"
        );
        setNewSponsor({ firstName: "", lastName: "", email: "", phone: "" });
        setOpenSponsorModal(false);
        setEditingSponsor(null);
        fetchSponsors(); // Refresh sponsors
      })
      .catch((error) => {
        console.error(error);
        showSnackbar(
          t(editingSponsor ? "sponsors.error.updateSponsor" : "sponsors.error.saveSponsor"),
          "error"
        );
      })
      .finally(() => setIsSavingSponsor(false));
  };

  const fetchSponsors = () => {
    fetchWithAuth(`${process.env.REACT_APP_BASE_URL}/sponsor/getSponsors/${communityId}`)
      .then((response) => response.json())
      .then((data) => setSponsors(data))
      .catch((error) => console.error(error));
  };

  const fetchAssignments = () => {
    fetchWithAuth(`${process.env.REACT_APP_BASE_URL}/sponsor/getSponsorsAssignments/${communityId}`)
      .then((response) => response.json())
      .then((data) => {
        setAssignments(data);
        filterActiveAssignments(data);
      })
      .catch((error) => console.error(error));
  };

  return (
    <Box>
      {isLoading ? (
        <Typography>{t("common.loading")}</Typography>
      ) : (
        <>
          {/* Snackbar */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
          >
            <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
              {snackbar.message}
            </Alert>
          </Snackbar>

          {/* Active Assignments Section */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                {t("sponsors.assignments.activeTitle")}
              </Typography>
              <Button variant="contained" color="primary" onClick={() => setOpenAssignmentModal(true)}>
                {t("sponsors.assignments.add")}
              </Button>
              <Grid container  sx={{ mt: 2 }}>
                {activeAssignments.map((assignment, index) => {
                  const sponsor = sponsors.find((s) => s.id === assignment.sponsorId) || {};
                  return (
                    <Grid item xs={12} md={6} lg={6} key={index} >
                      <Paper elevation={3} sx={{  padding: 2, borderRadius: 2, margin: 1  }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <Box>
                            <Typography variant="h6">{`${sponsor.firstName} ${sponsor.lastName}`}</Typography>
                          </Box>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <IconButton color="primary" onClick={() => handleEditAssignment(assignment)}>
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              color="error"
                              onClick={() =>
                                handleDeleteAssignment(assignment.communityId, assignment.sponsorId, assignment.startDate)
                              }
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Box>
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                          <strong>{t("sponsors.assignments.startDate")}:</strong> {assignment.startDate}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          <strong>{t("sponsors.assignments.endDate")}:</strong>{" "}
                          {assignment.endDate || t("sponsors.assignments.notSpecified")}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          <strong>{t("sponsors.assignments.reason")}:</strong>{" "}
                          {t(`sponsors.reasons.${assignment.reason}`)}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          <strong>{t("sponsors.assignments.comment")}:</strong>{" "}
                          {assignment.comment || t("sponsors.assignments.notSpecified")}
                        </Typography>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>

          {/* Sponsors Section */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                {t("sponsors.title")}
              </Typography>
              <Button variant="contained" color="secondary" onClick={() => setOpenSponsorModal(true)}>
                {t("sponsors.add")}
              </Button>

              {sponsors.map((sponsor, index) => (
                <Accordion key={index} sx={{ mt: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                      {/* Partie gauche : Texte et infos du sponsor */}
                      <Box>
                        <Typography variant="h6">{`${sponsor.firstName} ${sponsor.lastName}`}</Typography>
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth:  isSmallScreen ? "150px" : "250px", // Ajustez la largeur selon vos besoins
                            }}
                          >
                            {sponsor.email}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {sponsor.phone || t("common.phoneNotAvailable")}
                          </Typography>
                        </Box>
                      </Box>
                          {/* Partie droite : Boutons */}
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <IconButton color="primary" onClick={() => handleEditSponsor(sponsor)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton  color="error" onClick={() => handleDeleteSponsor(sponsor.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {assignments.filter((assignment) => assignment.sponsorId === sponsor.id).length === 0 ? (
                      <Typography variant="body2" color="textSecondary">
                        {t("sponsors.noAssignments")}
                      </Typography>
                    ) : (
                      assignments
                        .filter((assignment) => assignment.sponsorId === sponsor.id)
                        .map((assignment, idx) => (
                          <Paper elevation={1} sx={{ padding: 2, mb: 2 }} key={idx}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <Box>
                                <Typography variant="body2" color="textSecondary">
                                  <strong>{t("sponsors.assignments.startDate")}:</strong> {assignment.startDate}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  <strong>{t("sponsors.assignments.endDate")}:</strong>{" "}
                                  {assignment.endDate || t("sponsors.assignments.notSpecified")}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  <strong>{t("sponsors.assignments.reason")}:</strong> {t(`sponsors.reasons.${assignment.reason}`)}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  <strong>{t("sponsors.assignments.comment")}:</strong> {assignment.comment || t("sponsors.assignments.notSpecified")}
                                </Typography>
                              </Box>
                              <Box sx={{ display: "flex", gap: 1 }}>
                                {/* Bouton Edit */}
                                <IconButton color="primary" onClick={() => handleEditAssignment(assignment)}>
                                  <EditIcon />
                                </IconButton>

                                {/* Bouton Delete */}
                                <IconButton
                                  color="error"
                                  onClick={() =>
                                    handleDeleteAssignment(assignment.communityId, assignment.sponsorId, assignment.startDate)
                                  }
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            </Box>
                          </Paper>
                        ))
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}
            </CardContent>
          </Card>
                  {/* Assignment Modal */}
        <Modal open={openAssignmentModal} onClose={() => {
          setNewAssignment({ sponsorId: "", communityId: "", startDate: "", endDate: "", reason: "", comment: "" });
          setEditingAssignment(null);
          setOpenAssignmentModal(false);
        }}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
              width: 400,
            }}
          >
            <Typography variant="h6" gutterBottom>
              {editingAssignment
                ? t("sponsors.assignments.edit")
                : t("sponsors.assignments.add")}
            </Typography>
            <LocalizedTextField
              label={t("sponsors.assignments.sponsor")}
              select
              fullWidth
              value={newAssignment.sponsorId}
              onChange={(e) => handleAssignmentChange("sponsorId", e.target.value)}
              sx={{ mb: 2 }}
              helperText={t("sponsors.assignments.sponsorHelper")}
            >
              {sponsors.map((sponsor) => (
                <MenuItem key={sponsor.id} value={sponsor.id}>
                  {`${sponsor.firstName} ${sponsor.lastName}`}
                </MenuItem>
              ))}
            </LocalizedTextField>
            <LocalizedTextField
              label={t("sponsors.assignments.startDate")}
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={newAssignment.startDate}
              onChange={(e) => handleAssignmentChange("startDate", e.target.value)}
              sx={{ mb: 2 }}
            />
            <LocalizedTextField
              label={t("sponsors.assignments.endDate")}
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={newAssignment.endDate}
              onChange={(e) => handleAssignmentChange("endDate", e.target.value)}
              sx={{ mb: 2 }}
            />
            <LocalizedTextField
              label={t("sponsors.assignments.reason")}
              select
              fullWidth
              value={newAssignment.reason}
              onChange={(e) => handleAssignmentChange("reason", e.target.value)}
              sx={{ mb: 2 }}
            >
            {reasons.map((reason) => (
              <MenuItem key={reason} value={reason}>
                {t(`sponsors.reasons.${reason.toLowerCase()}`)}
              </MenuItem>
            ))}
            </LocalizedTextField>
            <LocalizedTextField
              label={t("sponsors.assignments.comment")}
              value={newAssignment.comment}
              onChange={(e) => handleAssignmentChange("comment", e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleSaveAssignment}
              disabled={isSavingAssignment}
            >
              {isSavingAssignment ? t("common.saving") : t("sponsors.assignments.save")}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              fullWidth
              sx={{ mt: 2 }}
              onClick={() => {
                setOpenAssignmentModal(false);
                setNewAssignment({ sponsorId: "", communityId: "", startDate: "", endDate: "", reason: "", comment: "" });
                setEditingAssignment(null);
              }}
            >
              {t("sponsors.assignments.cancel")}
            </Button>
          </Box>
        </Modal>

        {/* Sponsor Modal */}
        <Modal
          open={openSponsorModal}
          onClose={() => {
            setNewSponsor({ firstName: "", lastName: "", email: "", phone: "" });
            setEditingSponsor(null);
            setOpenSponsorModal(false);
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
              width: 400,
            }}
          >
            <Typography variant="h6" gutterBottom>
              {editingSponsor
                ? t("sponsors.edit.title", { name: `${editingSponsor.firstName} ${editingSponsor.lastName}` })
                : t("sponsors.create.title")}
            </Typography>
            <LocalizedTextField
              label={t("sponsors.create.firstName")}
              fullWidth
              value={newSponsor.firstName}
              onChange={(e) => handleSponsorChange("firstName", e.target.value)}
              sx={{ mb: 2 }}
            />
            <LocalizedTextField
              label={t("sponsors.create.lastName")}
              fullWidth
              value={newSponsor.lastName}
              onChange={(e) => handleSponsorChange("lastName", e.target.value)}
              sx={{ mb: 2 }}
            />
            <LocalizedTextField
              label={t("sponsors.create.email")}
              type="email"
              fullWidth
              value={newSponsor.email}
              onChange={(e) => handleSponsorChange("email", e.target.value)}
              sx={{ mb: 2 }}
            />
            <LocalizedTextField
              label={t("sponsors.create.phone")}
              fullWidth
              value={newSponsor.phone}
              onChange={(e) => handleSponsorChange("phone", e.target.value)}
              sx={{ mb: 2 }}
            />
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleSaveSponsor}
              disabled={isSavingSponsor}
            >
              {isSavingSponsor ? t("common.saving") : t("sponsors.create.save")}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              fullWidth
              sx={{ mt: 2 }}
              onClick={() => {
                setOpenSponsorModal(false);
                setNewSponsor({ firstName: "", lastName: "", email: "", phone: ""});
                setEditingSponsor(null);
              }}
            >
              {t("sponsors.create.cancel")}
            </Button>
          </Box>
        </Modal>
        </>
      )}
    </Box>
  );
};

export default SponsorManager;